import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Users } from "lucide-react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import WriteButton from "@/components/WriteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function CustomerManagement() {
  const { readOnly } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const term = search.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const { data } = await api.get("/customers", { params: term ? { q: term } : {}, signal: controller.signal });
        if (!controller.signal.aborted) setCustomers(data);
      } catch (e) {
        if (!controller.signal.aborted) setError(apiError(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, search ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [search, revision]);

  const save = async (event) => {
    event.preventDefault();
    if (readOnly || saving) return;
    if (!form.name.trim()) return toast.error("Nama pelanggan wajib diisi");
    setSaving(true);
    try {
      await api.post("/customers", { name: form.name.trim(), phone: form.phone.trim() || null });
      toast.success("Pelanggan ditambahkan");
      setOpen(false);
      setSearch("");
      setRevision((value) => value + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card data-testid="customer-management-card" className="min-w-0 self-start bg-ui-card border-ui-line text-ui-text shadow-none">
        <CardHeader className="p-4 border-b border-ui-line">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Users className="h-5 w-5 text-brand-readable" /> Pelanggan</h2>
              <CardDescription className="text-ui-muted mt-1">Daftar pelanggan toko dan sisa piutang.</CardDescription>
            </div>
            <WriteButton data-testid="add-customer-btn" onClick={() => { setForm({ name: "", phone: "" }); setOpen(true); }} className="bg-brand-button hover:bg-brand-hover text-ui-text gap-2">
              <Plus className="h-4 w-4" /> Tambah Pelanggan
            </WriteButton>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
            <Input data-testid="users-customer-search" aria-label="Cari pelanggan berdasarkan nama atau nomor HP" placeholder="Cari nama / no. HP pelanggan…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-ui-card border-ui-line" />
          </div>
          {loading ? (
            <p role="status" className="py-6 text-center text-sm text-ui-muted">Memuat pelanggan…</p>
          ) : error ? (
            <div role="alert" className="space-y-3 rounded-lg border border-danger bg-danger-soft p-4 text-sm text-danger-readable">
              <p>{error}</p>
              <Button variant="outline" onClick={() => setRevision((value) => value + 1)}>Coba lagi</Button>
            </div>
          ) : customers.length === 0 ? (
            <p data-testid="customers-empty" className="py-6 text-center text-sm text-ui-muted">{search.trim() ? "Pelanggan tidak ditemukan." : "Belum ada pelanggan. Tambahkan pelanggan pertama Anda."}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="customers-table">
                  <thead className="text-xs text-ui-muted uppercase tracking-wide">
                    <tr><th scope="col" className="text-left pb-3 pr-3">Nama / No. HP</th><th scope="col" className="text-right pb-3">Piutang</th></tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id} data-testid={`customer-row-${customer.id}`} className="border-t border-ui-line">
                        <td className="py-3 pr-3"><div className="font-medium break-words">{customer.name}</div><div className="text-xs text-ui-muted mt-1 break-all">{customer.phone || "No. HP belum diisi"}</div></td>
                        <td className={`py-3 text-right font-mono whitespace-nowrap ${customer.debt > 0 ? "text-danger-readable" : "text-ui-muted"}`}>{rupiah(customer.debt || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-ui-muted">{customers.length === 300 ? "Menampilkan hingga 300 pelanggan. Gunakan pencarian untuk mempersempit daftar." : `${customers.length} pelanggan ditampilkan`}</p>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(value) => { if (!saving) setOpen(value); }}>
        <DialogContent data-testid="customer-form-dialog" className="w-[calc(100%-2rem)] max-w-md bg-ui-card border-ui-line text-ui-text">
          <DialogHeader>
            <DialogTitle>Tambah Pelanggan</DialogTitle>
            <DialogDescription className="text-ui-muted">Pelanggan ini juga tersedia di kasir dan pencatatan piutang.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="managed-customer-name">Nama pelanggan</Label>
              <Input id="managed-customer-name" data-testid="managed-customer-name" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="managed-customer-phone">No. HP (opsional)</Label>
              <Input id="managed-customer-phone" data-testid="managed-customer-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={saving} onClick={() => setOpen(false)}>Batal</Button>
              <WriteButton type="submit" data-testid="save-customer-btn" disabled={saving} className="bg-brand-button hover:bg-brand-hover text-ui-text">{saving ? "Menyimpan…" : "Simpan Pelanggan"}</WriteButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
